import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { IconButton, Paper, Tooltip } from '@mui/material';
import { orange } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useMemo, useState } from 'react';
import {
  generatePath,
  Link,
  Link as NavLink,
  Route,
  Switch,
} from 'react-router-dom';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';

import {
  DataTable,
  IColDef,
} from 'src/app/modules/Manage/pages/queues-table/DataTable';

import { useBusinessPermissions } from '../../common/helperFunctions/useBusinessPermissions';
import { PopoverOnMouseOver } from '../../common/PopoverOnMouseOver';
import { trackRefreshQueuesLive } from '../../common/userTracking';
import { IUserAtom, Role, user } from '../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { QueueEditDialog } from '../modules/Manage/pages/queue-edit-dialog/QueueEditDialog';
import { QueueMembersDialog } from '../modules/Manage/pages/queue-edit-dialog/QueueMembersDialog';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  EnhancedTableToolbar,
  refreshDurations,
  ToolbarRefreshButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

const useStyles = makeStyles({
  peopleIconButton: {
    color: orange[500],
  },
});

const pad = (num) => {
  return ('0' + num).slice(-2);
};
const hhmmss = (secs) => {
  if (secs === 0) {
    return '00:00:00';
  }
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  const seconds = secs % 60;

  return `${hours}:${pad(minutes)}:${pad(seconds)}`;
};
const sortModel: GridSortModel = [
  {
    field: 'queuename',
    sort: 'asc',
  },
];

const actionFormatter = (params: GridRenderCellParams) => {
  const classes = useStyles();
  return (
    <Tooltip title="Members">
      <NavLink
        to={generatePath(
          '/call-reporting/live-queues/:id/members?queuename=' +
            String(params.row.queuename)
              .replaceAll('(', '%28')
              .replaceAll(')', '%29'),
          params.row
        )}
      >
        <IconButton name="members" size="large">
          <PeopleOutlineIcon className={classes.peopleIconButton} />
        </IconButton>
      </NavLink>
    </Tooltip>
  );
};

export function QueuesLive(props) {
  const [rowForEdit, handleRowForEdit] = useState<{ queueid?: string }>({});
  const [queueEditModal, handleQueueEditModal] = useState(false);
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents as IPermissions;
  const isUser = role === Role.USER;
  const handleEdit = (row) => {
    handleQueueEditModal(true);
    handleRowForEdit(row);
  };
  const readOnly = useBusinessPermissions().accountReadOnly;
  function nameFormatter(params: GridRenderCellParams) {
    return isUser ? (
      params.value
    ) : (
      <Tooltip title="Edit">
        {/* <Link to={`/manage/queues/${params.row.id}/edit`}>{params.value}</Link> */}
        <Link onClick={() => handleEdit(params.row)} to="#">
          {params.value}
        </Link>
      </Tooltip>
    );
  }
  const userAtom = useRecoilValueLoadable<IUserAtom | Record<string, unknown>>(
    user
  );
  const columns = [
    {
      field: 'queueid',
      headerName: 'Queue Id',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      // make queue id column hidden from users that are not a superadmin
      hide: (userAtom?.contents as IUserAtom)?.user?.role !== Role.SUPERADMIN,
    },
    {
      field: 'queuename',
      headerName: 'Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: nameFormatter,
    },
    {
      field: 'agentsSubscribed',
      headerName: '# Agents Subscribed',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'holding',
      headerName: 'Holding',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'avgholdtime',
      headerName: 'Avg. Hold Time',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <PopoverOnMouseOver
            displayValue={params.value}
            showPopover={params.row?.avgholdtime != '00:00:00'}
            showPopoverIcon={params.row?.avgholdtime != '00:00:00'}
            popoverTitle="Hold Time"
            popoverData={[
              { name: 'Minimum Time', value: params.row.minholdtime },
              { name: 'Average Time', value: params.row.avgholdtime },
              { name: 'Maximum Time', value: params.row.maxholdtime },
            ]}
          />
        );
      },
    },
    {
      field: 'requestCallback',
      headerName: 'Waiting For Callback',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'avgRequestCallbackTime',
      headerName: 'Avg. Callback Wait Time',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <PopoverOnMouseOver
            displayValue={params.value}
            showPopover={params?.row?.avgRequestCallbackTime != '00:00:00'}
            showPopoverIcon={params?.row?.avgRequestCallbackTime != '00:00:00'}
            popoverTitle="Waiting For Callback"
            popoverData={[
              {
                name: 'Minimum Time',
                value: params.row.minRequestCallbackTime,
              },
              {
                name: 'Average Time',
                value: params.row.avgRequestCallbackTime,
              },
              {
                name: 'Maximum Time',
                value: params.row.maxRequestCallbackTime,
              },
            ]}
          />
        );
      },
    },
    {
      field: 'id',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      renderCell: actionFormatter,
      hide: readOnly,
      disableSearch: true,
      enableSorting: false,
      width: 150,
    },
  ] /* satisfies */ as IColDef[];

  const refreshToggleForQueuesLive = useSetRecoilState(
    refetchToggle(endpointEnum.GET_QUEUES_LIVE_REPORTING)
  );
  const data = {
    call: methodNameEnum.GET_QUEUES_LIVE_REPORTING,
  };
  const queueTeamsLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_QUEUES_LIVE_REPORTING,
      payloadJsonString: JSON.stringify(data),
    })
  );

  const rows = useMemo(
    () =>
      queueTeamsLoadable.contents?.response?.data?.result
        ?.map((o) => ({
          ...o,
          id: o.queueid,
          queueid: o.queueid.toString(),
          queuename: o?.queuename || 'no name',
          holding: o.holding || 0,
          minholdtime: hhmmss(o.minholdtime),
          avgholdtime: hhmmss(o.avgholdtime),
          maxholdtime: hhmmss(o.maxholdtime),
          requestCallback: o.requestCallback || 0,
          minRequestCallbackTime: hhmmss(o.minRequestCallbackTime),
          avgRequestCallbackTime: hhmmss(o.avgRequestCallbackTime),
          maxRequestCallbackTime: hhmmss(o.maxRequestCallbackTime),
          agentsSubscribed: o.agentsSubscribed || 0,
        }))
        .filter((q) => q.queueid !== 0 && q.queuename) || [],
    [queueTeamsLoadable]
  );
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        dataType="QueuesLive"
        {...props}
        rightComponent={
          <ToolbarRefreshButton
            id="QueuesLive"
            duration={refreshDurations.dur30sec}
            onClickForRefresh={() => {
              trackRefreshQueuesLive();
              refreshToggleForQueuesLive({});
            }}
            isLoading={props.isLoading}
          />
        }
      />
    ),
    []
  );
  const [updatedRowId, setUpdatedRowId] = useState<string | undefined>();
  return (
    <>
      <Switch>
        <Route path="/call-reporting/live-queues/:id/members">
          {({ history, match }) => (
            <QueueMembersDialog
              show={match != null}
              id={match?.params.id}
              setUpdatedRowId={setUpdatedRowId}
              onHide={() => {
                history.push('/call-reporting/live-queues');
              }}
            />
          )}
        </Route>
      </Switch>
      <QueueEditDialog
        show={!!queueEditModal}
        id={rowForEdit.queueid}
        onHide={() => {
          handleQueueEditModal(false);
          refreshToggleForQueuesLive({});
        }}
      />
      <Paper square={true} elevation={0}>
        <DataTable
          columns={columns}
          rows={rows}
          loading={queueTeamsLoadable.state === 'loading'}
          sortModel={sortModel}
          updatedRowId={updatedRowId}
          ToolbarComponent={ToolbarComponent}
        />
      </Paper>
    </>
  );
}
